import type { IEntriToken } from '@feathr/blackbox';
import { RachisError } from '@feathr/rachis';

import type { IEntriCloseDetail, IEntriConfig } from './entri';

export enum EEntriOutcome {
  Dismissed = 'dismissed',
  Error = 'error',
  LinkShared = 'linkShared',
  Success = 'success',
}

export enum EEntriTokenError {
  Unavailable = 'unavailable',
  Unsupported = 'unsupported',
}

const LINK_SHARED_STATUS = 'FINISHED_SUCCESSFULLY_LINK_SHARED';
const EXIT_WITH_ERROR_STATUS = 'EXIT_WITH_ERROR';
const UNKNOWN_PROVIDER = 'unknown';

// A shared link closes with success: false, so it is checked before errors and success.
export function getEntriOutcome(detail: IEntriCloseDetail): EEntriOutcome {
  if (detail.lastStatus === LINK_SHARED_STATUS) {
    return EEntriOutcome.LinkShared;
  }
  if (detail.error || detail.lastStatus === EXIT_WITH_ERROR_STATUS) {
    return EEntriOutcome.Error;
  }
  if (detail.success) {
    return EEntriOutcome.Success;
  }
  return EEntriOutcome.Dismissed;
}

/** Returns the detected DNS host worth storing, or undefined when Entri couldn't identify one. */
export function getStorableProvider(detail: IEntriCloseDetail): string | undefined {
  const { provider } = detail;
  if (!provider || provider.toLowerCase() === UNKNOWN_PROVIDER) {
    return undefined;
  }
  return provider;
}

// The backend answers 400 when a domain has no email domain or a record outside its zone.
export function getEntriTokenError(error: unknown): EEntriTokenError {
  const errors = error instanceof AggregateError ? error.errors : [];
  const isBadRequest = errors.some(
    (item: unknown) => item instanceof RachisError && Number(item.status) === 400,
  );
  return isBadRequest ? EEntriTokenError.Unsupported : EEntriTokenError.Unavailable;
}

export function toEntriConfig(token: IEntriToken): IEntriConfig {
  return {
    applicationId: token.application_id,
    dnsRecords: token.dns_records,
    prefilledDomain: token.domain,
    token: token.token,
  };
}
